import { useMemo } from 'react';
import { useApp } from '@/contexts/AppContext';
import { calculateDiagramStats, getBoundaryChain } from '@/lib/indexers';
import type { EntityType } from '@/types';

export function useDiagramData() {
  const { currentDiagram, entitiesMap, boundariesMap } = useApp();

  const stats = useMemo(() => {
    if (!currentDiagram) return null;
    return calculateDiagramStats(currentDiagram);
  }, [currentDiagram]);

  const pathsWithoutAuth = useMemo(() => {
    if (!currentDiagram) return 0;
    return currentDiagram.paths.filter((p) => !p.authentication || p.authentication === '').length;
  }, [currentDiagram]);

  const pathsWithoutName = useMemo(() => {
    if (!currentDiagram) return 0;
    return currentDiagram.paths.filter((p) => !p.name || p.name === '').length;
  }, [currentDiagram]);

  const protocols = useMemo(() => {
    if (!currentDiagram) return [];
    return Array.from(new Set(currentDiagram.paths.map((p) => p.protocol)));
  }, [currentDiagram]);

  const mostConnectedEntity = useMemo(() => {
    if (!currentDiagram) return null;

    const connections: Record<number, number> = {};
    currentDiagram.paths.forEach((path) => {
      connections[path.source_id] = (connections[path.source_id] || 0) + 1;
      connections[path.target_id] = (connections[path.target_id] || 0) + 1;
    });

    const keys = Object.keys(connections);
    if (keys.length === 0) return null;

    const mostConnectedId = keys.reduce((a, b) =>
      connections[Number(a)] > connections[Number(b)] ? a : b
    );

    return {
      entity: entitiesMap[Number(mostConnectedId)],
      count: connections[Number(mostConnectedId)],
    };
  }, [currentDiagram, entitiesMap]);

  const entityTypeDistribution = useMemo(() => {
    if (!currentDiagram) return {};

    const typeCounts: Record<EntityType, number> = {
      default: 0,
      external: 0,
      user: 0,
      process: 0,
      storage: 0,
    };

    currentDiagram.entities.forEach((entity) => {
      const type = entity.type || 'default';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    return typeCounts;
  }, [currentDiagram]);

  const scopedEntities = useMemo(() => {
    if (!currentDiagram) return { inScope: [], outOfScope: [] };

    const inScope: string[] = [];
    const outOfScope: string[] = [];

    currentDiagram.entities.forEach((entity) => {
      if (entity.type === 'external') {
        outOfScope.push(entity.name);
      } else {
        inScope.push(entity.name);
      }
    });

    return {
      inScope: inScope.sort(),
      outOfScope: outOfScope.sort(),
    };
  }, [currentDiagram]);

  const getEntityBoundaryChain = useMemo(() => {
    return (boundaryId: number | undefined | null) => getBoundaryChain(boundaryId, boundariesMap);
  }, [boundariesMap]);

  return {
    currentDiagram,
    stats,
    pathsWithoutAuth,
    pathsWithoutName,
    protocols,
    mostConnectedEntity,
    entityTypeDistribution,
    scopedEntities,
    getEntityBoundaryChain,
    entitiesMap,
    boundariesMap,
  };
}
